import { useState, useMemo, type FormEvent } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type StrategyConfig, type Rule, type MetricKey, type CompareOp, type Action, type StrategyDetail, type PreviewDecision } from '../api';
import { useAuth } from '../auth';

const METRICS: { key: MetricKey; label: string; isString?: boolean; premium?: boolean }[] = [
  { key: 'stock_total', label: 'Остатки (всего)' },
  { key: 'turnover_days', label: 'Оборачиваемость (дней)' },
  { key: 'ext_price_index', label: 'Индекс vs внешние ' },
  { key: 'ozon_price_index', label: 'Индекс vs Ozon ' },
  { key: 'color_index', label: 'Цветовой индекс', isString: true },
  { key: 'ordered_units_7d', label: 'Заказы за 7 дней' },
  { key: 'revenue_7d', label: 'Выручка за 7 дней' },
  { key: 'hits_view_7d', label: 'Просмотры за 7 дней', premium: true },
  { key: 'conv_tocart_7d', label: 'Конверсия в корзину', premium: true },
  { key: 'position_category', label: 'Позиция в категории', premium: true },
  { key: 'buyout_rate_30d', label: '% выкупа за 30 дней' },
];

const OPS: { key: CompareOp; label: string }[] = [
  { key: 'lt', label: '<' }, { key: 'lte', label: '≤' },
  { key: 'gt', label: '>' }, { key: 'gte', label: '≥' },
  { key: 'eq', label: '=' }, { key: 'neq', label: '≠' },
];

const ACTION_KINDS: { kind: Action['kind']; label: string }[] = [
  { kind: 'set_price', label: 'Установить цену' },
  { kind: 'increase_pct', label: 'Увеличить на %' },
  { kind: 'decrease_pct', label: 'Уменьшить на %' },
  { kind: 'increase_abs', label: 'Увеличить на ₽' },
  { kind: 'decrease_abs', label: 'Уменьшить на ₽' },
  { kind: 'match_ext_min', label: 'Подстроиться под внешний минимум' },
];

const CRON_PRESETS = [
  { value: '*/15 * * * *', label: 'Каждые 15 минут' },
  { value: '*/30 * * * *', label: 'Каждые 30 минут' },
  { value: '0 * * * *', label: 'Каждый час' },
  { value: '0 */3 * * *', label: 'Каждые 3 часа' },
  { value: '0 9 * * *', label: 'Раз в день в 9:00' },
];

const emptyConfig: StrategyConfig = {
  cooldownMin: 60,
  dryRun: false,
  enforceBreakeven: true,
  minMargin: 0.05,
  rules: [
    {
      name: 'Поднять цену при заканчивающемся остатке',
      when: [{ metric: 'stock_total', op: 'lt', value: 5 }],
      then: { kind: 'increase_pct', value: 10 },
    },
  ],
};

export function StrategyEditPage() {
  const { id } = useParams();
  const isNew = !id;
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const detail = useQuery({
    queryKey: ['strategy', id],
    queryFn: () => api.strategyGet(Number(id)),
    enabled: !isNew,
  });

  const [name, setName] = useState('Новая стратегия');
  const [scheduleCron, setScheduleCron] = useState('*/15 * * * *');
  const [enabled, setEnabled] = useState(false);
  const [config, setConfig] = useState<StrategyConfig>(emptyConfig);
  const [initialized, setInitialized] = useState(false);

  // Подгружаем существующую стратегию в состояние формы один раз
  if (!isNew && detail.data && !initialized) {
    setName(detail.data.name);
    setScheduleCron(detail.data.scheduleCron);
    setEnabled(detail.data.enabled);
    setConfig(detail.data.config);
    setInitialized(true);
  }

  const create = useMutation({
    mutationFn: () => api.strategyCreate({ name, scheduleCron, enabled, config }),
    onSuccess: (r) => { qc.invalidateQueries({ queryKey: ['strategies'] }); navigate(`/strategies/${r.id}`); },
  });
  const update = useMutation({
    mutationFn: () => api.strategyUpdate(Number(id), { name, scheduleCron, enabled, config }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['strategies'] }); qc.invalidateQueries({ queryKey: ['strategy', id] }); },
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    isNew ? create.mutate() : update.mutate();
  };

  const updateRule = (i: number, patch: Partial<Rule>) => {
    setConfig({ ...config, rules: config.rules.map((r, j) => (i === j ? { ...r, ...patch } : r)) });
  };
  const addRule = () => {
    setConfig({
      ...config,
      rules: [...config.rules, { name: `Правило ${config.rules.length + 1}`, when: [{ metric: 'stock_total', op: 'gt', value: 0 }], then: { kind: 'increase_pct', value: 0 } }],
    });
  };
  const removeRule = (i: number) => setConfig({ ...config, rules: config.rules.filter((_, j) => j !== i) });

  return (
    <>
      <div className="page-header">
        <h2>{isNew ? 'Новая стратегия' : `Стратегия #${id}`}</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          {!isNew && <Link to={`/strategies/${id}/products`} className="btn">Товары и прогон</Link>}
          <Link to="/strategies" className="btn">← К списку</Link>
        </div>
      </div>

      <form onSubmit={onSubmit}>
        <div className="card" style={{ marginBottom: 16 }}>
          <h3 style={{ marginTop: 0 }}>Основное</h3>
          <Row label="Название"><input type="text" required value={name} onChange={(e) => setName(e.target.value)} className="input" style={{ width: 360 }} disabled={!isAdmin} /></Row>
          <Row label="Cron">
            <select value={scheduleCron} onChange={(e) => setScheduleCron(e.target.value)} className="input" style={{ width: 240 }} disabled={!isAdmin}>
              {CRON_PRESETS.map((p) => <option key={p.value} value={p.value}>{p.label} — {p.value}</option>)}
              {!CRON_PRESETS.find((p) => p.value === scheduleCron) && <option value={scheduleCron}>{scheduleCron}</option>}
            </select>
          </Row>
          <Row label="Активна">
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} disabled={!isAdmin} />
          </Row>
        </div>

        <div className="card" style={{ marginBottom: 16 }}>
          <h3 style={{ marginTop: 0 }}>Защита и ограничения</h3>
          <Row label="Cooldown (мин)" hint="Не менять цену чаще чем раз в N мин. По умолчанию 60 — защита от лимита Ozon (10 апдейтов/час/товар).">
            <input type="number" min={1} value={config.cooldownMin} onChange={(e) => setConfig({ ...config, cooldownMin: Number(e.target.value) })} className="input" style={{ width: 120 }} disabled={!isAdmin} />
          </Row>
          <Row label="Dry-run" hint="Только считать, не отправлять в Ozon.">
            <input type="checkbox" checked={config.dryRun} onChange={(e) => setConfig({ ...config, dryRun: e.target.checked })} disabled={!isAdmin} />
          </Row>
          <Row label="Учёт breakeven" hint="Не опускать цену ниже суммы комиссий Ozon + минимальная маржа.">
            <input type="checkbox" checked={config.enforceBreakeven} onChange={(e) => setConfig({ ...config, enforceBreakeven: e.target.checked })} disabled={!isAdmin} />
          </Row>
          <Row label="Мин. маржа (доля)" hint="0.05 = 5% поверх комиссий.">
            <input type="number" step="0.01" min={0} max={0.95} value={config.minMargin} onChange={(e) => setConfig({ ...config, minMargin: Number(e.target.value) })} className="input" style={{ width: 120 }} disabled={!isAdmin} />
          </Row>
          <Row label="Global Min ₽">
            <input type="number" min={0} value={config.globalMinPrice ?? ''} onChange={(e) => setConfig({ ...config, globalMinPrice: e.target.value ? Number(e.target.value) : undefined })} className="input" style={{ width: 120 }} disabled={!isAdmin} />
          </Row>
          <Row label="Global Max ₽">
            <input type="number" min={0} value={config.globalMaxPrice ?? ''} onChange={(e) => setConfig({ ...config, globalMaxPrice: e.target.value ? Number(e.target.value) : undefined })} className="input" style={{ width: 120 }} disabled={!isAdmin} />
          </Row>
        </div>

        <div className="card" style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h3 style={{ margin: 0 }}>Правила</h3>
            <button type="button" className="btn" onClick={addRule} disabled={!isAdmin}>+ Добавить правило</button>
          </div>
          <p className="muted" style={{ fontSize: 12, marginTop: 0 }}>
            Правила проверяются сверху вниз. Применяется первое, у которого все условия выполнены.
          </p>
          {config.rules.map((r, i) => <RuleEditor key={i} rule={r} onChange={(patch) => updateRule(i, patch)} onRemove={() => removeRule(i)} disabled={!isAdmin} />)}
        </div>

        {isAdmin && (
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="submit" className="btn primary" disabled={create.isPending || update.isPending}>
              {isNew ? (create.isPending ? 'Создание…' : 'Создать') : (update.isPending ? 'Сохранение…' : 'Сохранить')}
            </button>
            {!isNew && (
              <button type="button" className="btn" onClick={() => { if (confirm('Удалить стратегию?')) api.strategyDelete(Number(id)).then(() => navigate('/strategies')); }}>
                Удалить
              </button>
            )}
          </div>
        )}
      </form>
    </>
  );
}

function Row({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>
        {label}{hint && <span style={{ marginLeft: 6, fontStyle: 'italic' }}>— {hint}</span>}
      </div>
      {children}
    </div>
  );
}

function RuleEditor({ rule, onChange, onRemove, disabled }: { rule: Rule; onChange: (patch: Partial<Rule>) => void; onRemove: () => void; disabled?: boolean }) {
  const setCond = (i: number, patch: Partial<{ metric: MetricKey; op: CompareOp; value: number | string }>) => {
    onChange({ when: rule.when.map((c, j) => (i === j ? { ...c, ...patch } : c)) });
  };
  const addCond = () => onChange({ when: [...rule.when, { metric: 'stock_total', op: 'gt', value: 0 }] });
  const removeCond = (i: number) => onChange({ when: rule.when.filter((_, j) => j !== i) });

  const setAction = (patch: Partial<Action>) => {
    onChange({ then: { ...rule.then, ...patch } as Action });
  };

  return (
    <div style={{ padding: 12, border: '1px solid var(--border)', borderRadius: 6, marginBottom: 12, background: '#fafbfc' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <input type="text" value={rule.name} onChange={(e) => onChange({ name: e.target.value })} className="input" style={{ width: 320, fontWeight: 600 }} disabled={disabled} />
        <button type="button" className="btn" onClick={onRemove} disabled={disabled}>Удалить</button>
      </div>

      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>ЕСЛИ (все условия):</div>
      {rule.when.map((c, i) => {
        const isString = METRICS.find((m) => m.key === c.metric)?.isString;
        return (
          <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6, alignItems: 'center' }}>
            <select value={c.metric} onChange={(e) => setCond(i, { metric: e.target.value as MetricKey })} className="input" disabled={disabled}>
              {METRICS.map((m) => <option key={m.key} value={m.key}>{m.label}{m.premium ? ' (Premium)' : ''}</option>)}
            </select>
            <select value={c.op} onChange={(e) => setCond(i, { op: e.target.value as CompareOp })} className="input" style={{ width: 60 }} disabled={disabled || (isString && c.op !== 'eq' && c.op !== 'neq')}>
              {OPS.filter((o) => !isString || o.key === 'eq' || o.key === 'neq').map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
            </select>
            {isString ? (
              <select value={String(c.value)} onChange={(e) => setCond(i, { value: e.target.value })} className="input" disabled={disabled}>
                <option value="WITHOUT_INDEX">WITHOUT_INDEX</option>
                <option value="GREEN">GREEN</option>
                <option value="YELLOW">YELLOW</option>
                <option value="RED">RED</option>
              </select>
            ) : (
              <input type="number" step="any" value={c.value as number} onChange={(e) => setCond(i, { value: Number(e.target.value) })} className="input" style={{ width: 120 }} disabled={disabled} />
            )}
            <button type="button" className="btn" onClick={() => removeCond(i)} disabled={disabled || rule.when.length === 1}>×</button>
          </div>
        );
      })}
      <button type="button" className="btn" onClick={addCond} disabled={disabled} style={{ marginBottom: 8 }}>+ Условие (AND)</button>

      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4, marginTop: 8 }}>ТО:</div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <select
          value={rule.then.kind}
          onChange={(e) => {
            const kind = e.target.value as Action['kind'];
            const fresh: Action = kind === 'match_ext_min' ? { kind, offsetPct: 0 } : ({ kind, value: 0 } as Action);
            onChange({ then: fresh });
          }}
          className="input"
          disabled={disabled}
        >
          {ACTION_KINDS.map((a) => <option key={a.kind} value={a.kind}>{a.label}</option>)}
        </select>
        {rule.then.kind === 'match_ext_min' ? (
          <>
            <input type="number" step="any" value={rule.then.offsetPct} onChange={(e) => setAction({ offsetPct: Number(e.target.value) } as Partial<Action>)} className="input" style={{ width: 120 }} disabled={disabled} />
            <span className="muted" style={{ fontSize: 12 }}>% от внешнего минимума</span>
          </>
        ) : (
          <input type="number" step="any" value={(rule.then as { value: number }).value} onChange={(e) => setAction({ value: Number(e.target.value) } as Partial<Action>)} className="input" style={{ width: 120 }} disabled={disabled} />
        )}
      </div>
    </div>
  );
}
